import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { format, parseISO } from "date-fns";
import {
  LifeBuoy,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Clock,
  AlertCircle,
  CheckCircle,
  XCircle,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  open: "bg-red-500/20 text-red-400 border-red-500/30",
  in_progress: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  resolved: "bg-green-500/20 text-green-400 border-green-500/30",
  closed: "bg-neutral-500/20 text-neutral-400 border-neutral-500/30",
};

const PRIORITY_COLORS = {
  low: "bg-neutral-500/20 text-neutral-400 border-neutral-500/30",
  medium: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  high: "bg-red-500/20 text-red-400 border-red-500/30",
};

export const AdminSupport = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [expandedTickets, setExpandedTickets] = useState({});
  const [updating, setUpdating] = useState(null);
  const [stats, setStats] = useState({ open: 0, in_progress: 0, resolved: 0 });
  const limit = 10;

  useEffect(() => {
    fetchTickets();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, statusFilter, priorityFilter]);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [openRes, inProgressRes, resolvedRes] = await Promise.all([
        axios.get(`${API}/admin/support/tickets?status=open&limit=1`),
        axios.get(`${API}/admin/support/tickets?status=in_progress&limit=1`),
        axios.get(`${API}/admin/support/tickets?status=resolved&limit=1`),
      ]);
      setStats({
        open: openRes.data.total || 0,
        in_progress: inProgressRes.data.total || 0,
        resolved: resolvedRes.data.total || 0,
      });
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const skip = (page - 1) * limit;
      let url = `${API}/admin/support/tickets?limit=${limit}&skip=${skip}`;
      if (statusFilter !== "all") {
        url += `&status=${statusFilter}`;
      }
      if (priorityFilter !== "all") {
        url += `&priority=${priorityFilter}`;
      }
      const res = await axios.get(url);
      setTickets(res.data.tickets || []);
      setTotal(res.data.total || 0);
    } catch (error) {
      console.error("Error fetching tickets:", error);
      toast.error("Failed to load tickets");
    } finally {
      setLoading(false);
    }
  };

  const updateTicketStatus = async (ticketId, newStatus) => {
    setUpdating(ticketId);
    try {
      await axios.put(`${API}/admin/support/tickets/${ticketId}`, { status: newStatus });
      toast.success(`Ticket marked as ${newStatus.replace("_", " ")}`);
      fetchTickets();
      fetchStats();
    } catch (error) {
      toast.error("Failed to update ticket");
    } finally {
      setUpdating(null);
    }
  };

  const toggleExpand = (ticketId) => {
    setExpandedTickets((prev) => ({
      ...prev,
      [ticketId]: !prev[ticketId],
    }));
  };

  const totalPages = Math.ceil(total / limit);

  const getNextAction = (status) => {
    switch (status) {
      case "open":
        return { label: "Mark In Progress", nextStatus: "in_progress" };
      case "in_progress":
        return { label: "Mark Resolved", nextStatus: "resolved" };
      case "resolved":
        return { label: "Close", nextStatus: "closed" };
      case "closed":
        return { label: "Reopen", nextStatus: "open" };
      default:
        return null;
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-white">Support Tickets</h1>
        <p className="text-neutral-400 text-sm mt-1">Manage customer support requests</p>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-3 gap-3 md:gap-4 mb-6">
        <Card className="card-empire">
          <CardContent className="py-3 text-center">
            <p className="text-2xl font-bold text-red-400">{stats.open}</p>
            <p className="text-xs text-neutral-400">Open</p>
          </CardContent>
        </Card>
        <Card className="card-empire">
          <CardContent className="py-3 text-center">
            <p className="text-2xl font-bold text-yellow-400">{stats.in_progress}</p>
            <p className="text-xs text-neutral-400">In Progress</p>
          </CardContent>
        </Card>
        <Card className="card-empire">
          <CardContent className="py-3 text-center">
            <p className="text-2xl font-bold text-green-400">{stats.resolved}</p>
            <p className="text-xs text-neutral-400">Resolved</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="card-empire mb-6">
        <CardContent className="py-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <label className="text-xs text-neutral-400 mb-1 block">Status</label>
              <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
                <SelectTrigger className="bg-neutral-900 border-neutral-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-neutral-900 border-neutral-700">
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <label className="text-xs text-neutral-400 mb-1 block">Priority</label>
              <Select value={priorityFilter} onValueChange={(v) => { setPriorityFilter(v); setPage(1); }}>
                <SelectTrigger className="bg-neutral-900 border-neutral-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-neutral-900 border-neutral-700">
                  <SelectItem value="all">All Priorities</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tickets List */}
      <Card className="card-empire">
        <CardHeader className="pb-3">
          <CardTitle className="text-white text-base flex items-center justify-between">
            <span className="flex items-center gap-2">
              <LifeBuoy className="w-4 h-4 text-empire-gold" />
              Tickets
            </span>
            <Badge variant="outline" className="border-neutral-700 text-neutral-400">
              {total} total
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 text-empire-gold animate-spin" />
            </div>
          ) : tickets.length === 0 ? (
            <div className="text-center py-12 text-neutral-500">
              <LifeBuoy className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No tickets found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {tickets.map((ticket, index) => {
                const isExpanded = expandedTickets[ticket.id];
                const nextAction = getNextAction(ticket.status);
                
                return (
                  <motion.div
                    key={ticket.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.03 }}
                    className="p-4 bg-neutral-900/50 rounded-lg border border-neutral-800"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className="text-xs font-mono text-neutral-500">
                            {ticket.ticket_number}
                          </span>
                          <Badge className={STATUS_COLORS[ticket.status] || STATUS_COLORS.open}>
                            {ticket.status?.replace("_", " ")}
                          </Badge>
                          {ticket.priority && (
                            <Badge className={PRIORITY_COLORS[ticket.priority] || PRIORITY_COLORS.low}>
                              {ticket.priority}
                            </Badge>
                          )}
                        </div>
                        <h3 className="font-medium text-white mb-1">{ticket.subject}</h3>
                        <div className="text-xs text-neutral-400 space-y-0.5">
                          <p>{ticket.guest_name} • {ticket.guest_email}</p>
                          {ticket.guest_phone && <p>{ticket.guest_phone}</p>}
                          <p className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {ticket.created_at && format(parseISO(ticket.created_at), "MMM d, yyyy 'at' h:mm a")}
                          </p>
                        </div>
                        
                        {ticket.message && (
                          <div className="mt-2">
                            <p className={`text-sm text-neutral-300 ${!isExpanded ? "line-clamp-2" : ""}`}>
                              {ticket.message}
                            </p>
                            {ticket.message.length > 150 && (
                              <button
                                onClick={() => toggleExpand(ticket.id)}
                                className="text-xs text-empire-gold hover:underline mt-1 flex items-center gap-1"
                              >
                                {isExpanded ? (
                                  <>Show less <ChevronUp className="w-3 h-3" /></>
                                ) : (
                                  <>Show more <ChevronDown className="w-3 h-3" /></>
                                )}
                              </button>
                            )}
                          </div>
                        )}
                        
                        {ticket.booking_number && (
                          <p className="text-xs text-neutral-500 mt-2">
                            Related booking: <span className="font-mono">{ticket.booking_number}</span>
                          </p>
                        )}
                      </div>
                      
                      <div className="flex sm:flex-col gap-2">
                        {nextAction && (
                          <Button
                            size="sm"
                            variant={ticket.status === "open" ? "default" : "outline"}
                            className={ticket.status === "open" ? "btn-empire" : "border-neutral-700"}
                            onClick={() => updateTicketStatus(ticket.id, nextAction.nextStatus)}
                            disabled={updating === ticket.id}
                          >
                            {updating === ticket.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              nextAction.label
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6 pt-4 border-t border-neutral-800">
              <p className="text-sm text-neutral-400">
                Page {page} of {totalPages}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="border-neutral-700"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="border-neutral-700"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
